import { UserId } from '@cibo/core'
import { useOrgUser } from '@cibo/profile'
import { FriendlyError } from '@cibo/ui'
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath } from 'react-router-dom'
import { GROWERS_ROUTES } from '../../navigation/types/growersRoutes'
import { UserProgramCard } from './UserProgramCard'
import { UserProgramCardData } from './UserProgramCardTypes'
import { UserProgramStatusPicker } from './UserProgramStatusPicker'
import { useUserProgramStatusCards } from './useUserProgramStatusCards'

interface UserProgramsListProps {
  userId: UserId
}

export const UserProgramsList = ({ userId }: UserProgramsListProps) => {
  const { t } = useTranslation('impact/UserProgramsList')

  const [statusFilter, setStatusFilter] = useState('allStatuses')

  const filterByStatus = useCallback(
    (card: UserProgramCardData) => {
      if (statusFilter === 'allStatuses') {
        return true
      }

      return card.cardStatus === statusFilter
    },
    [statusFilter]
  )

  const { data: cards, isLoading, isError } = useUserProgramStatusCards({ userId })
  const user = useOrgUser(userId)

  if (isLoading) {
    return <Skeleton variant="rounded" height={200} />
  }

  if (isError) {
    return <FriendlyError message={t('errorLoading')} />
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems={'center'}>
        <Typography fontSize={20}>
          {t('usersPrograms', { name: user.data?.displayNameFull })}
        </Typography>

        <UserProgramStatusPicker onChange={setStatusFilter} value={statusFilter} />
      </Stack>

      <Box>
        <Grid columns={{ xs: 4, md: 8, lg: 12 }} spacing={3} container>
          {cards?.filter(filterByStatus).map(userProgramData => (
            <Grid key={userProgramData.programId} xs={4} item>
              <UserProgramCard
                LinkComponent={Link}
                userProgramData={userProgramData}
                to={generatePath(GROWERS_ROUTES.PROGRAM, {
                  userId: userId.toString(),
                  programId: userProgramData.programId,
                })}
                sx={{ flex: 1, height: '100%' }}
                userId={userId}
                variant="outlined"
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  )
}
